import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { AutoComplete, IOption, useAsyncAutocomplete } from "@evergis/ui";
import { QueryLayerServiceConfigurationDc } from "@evergis/api";

import { TextFilterContainer } from "./styled";
import { getConfigFilter } from "../../utils/getConfigFilter";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { useUpdateDataSource } from "../../../Dashboard/hooks/useUpdateDataSource";
import { useRelatedDataSourceAttributes } from "../../hooks/useRelatedDataSourceAttributes";
import { useLayerParams } from "../../../../hooks/eql/useLayerParams";
import { WidgetFilterProps } from "./types";
import { SelectedFilters } from "../../types";

export const TextFilter: FC<WidgetFilterProps> = ({ type, filter, config }) => {
  const { filters, changeFilters, dataSources } = useWidgetContext(type);
  const suggestRef = useRef<HTMLElement | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const { filters: configFilters, dataSources: configDataSources } = config;
  const { filterName, searchFilterName, placeholder, width, height, multiSelect, variants } = filter.options;

  const { attributes, layerInfo } = useRelatedDataSourceAttributes({ config, elementConfig: filter, dataSources });
  const { eqlParameters } = (layerInfo?.configuration || {}) as QueryLayerServiceConfigurationDc;
  const [layerParams] = useLayerParams(layerInfo);
  const [value, setValue] = useState("");

  const configFilter = useMemo(() => getConfigFilter(filterName, configFilters), [configFilters, filterName]);

  const searchFilter = useMemo(
    () => getConfigFilter(searchFilterName, configFilters),
    [configFilters, searchFilterName],
  );
  const searchDataSource = useMemo(
    () =>
      !!searchFilter?.relatedDataSource &&
      configDataSources.find(({ name }) => name === searchFilter.relatedDataSource),
    [configDataSources, searchFilter?.relatedDataSource],
  );

  const updateDataSource = useUpdateDataSource({
    dataSource: searchDataSource,
    config,
    filters,
    attributes,
    layerParams,
    eqlParameters,
  });

  const [options, setOptions] = useState<IOption[]>([]);

  const setFilterValue = useCallback(
    newValue => {
      changeFilters(
        {
          [filterName]: {
            value: newValue,
          },
        },
        configFilter?.resetFilters,
      );
    },
    [changeFilters, configFilter?.resetFilters, filterName],
  );

  const onSelect = useCallback(
    (newSelected: IOption[]) => {
      const lastSelected = newSelected[newSelected.length - 1];

      setFilterValue(
        multiSelect
          ? newSelected.map(item => item.value)
          : newSelected[newSelected.length - 1]?.value
          ? [newSelected[newSelected.length - 1]?.value]
          : [],
      );
      setValue(multiSelect ? "" : lastSelected?.text || "");
    },
    [multiSelect, setFilterValue],
  );

  const onBlur = useCallback(() => {
    if (variants) {
      setFilterValue([value]);
    } else {
      setOptions([]);
    }
  }, [variants, setFilterValue, value]);

  const onKeyUp = useCallback(
    e => {
      if (e.key === "Enter") {
        setFilterValue([value]);
      }
    },
    [setFilterValue, value],
  );

  const { asyncCallback, loadNext } = useAsyncAutocomplete({
    options,
    setOptions: variants ? () => {} : setOptions,
    setTotal: setTotalCount,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatTotal: (data: Record<string, any>) => data.totalCount,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatOptions: (data: Record<string, any>) => {
      return data.items.map(item => ({
        text: (item.attributes.attributes || item.attributes)[searchFilter.attributeAlias],
        value: (item.attributes.attributes || item.attributes)[searchFilter.attributeValue],
      }));
    },
  });

  const search = useCallback(
    (searchValue: string, isLoadNext = false): Promise<void> => {
      if (variants || !searchFilter?.attributeAlias || !searchFilter?.attributeValue) return;

      const newFilter: SelectedFilters = {
        [searchFilterName]: {
          value: `%${searchValue || ""}%`,
        },
      };

      asyncCallback({
        value: searchValue,
        promise: updateDataSource(newFilter, isLoadNext ? options.length : 0),
        isLoadNext,
      });
    },
    [
      variants,
      searchFilter?.attributeAlias,
      searchFilter?.attributeValue,
      searchFilterName,
      asyncCallback,
      updateDataSource,
      options.length,
    ],
  );

  const infiniteScrollProps = useMemo(
    () => ({
      loadNext: () => loadNext(() => search(value, true)),
      hasMore: totalCount > options.length,
    }),
    [totalCount, options.length, loadNext, search, value],
  );

  const selected: string[] = useMemo(() => {
    if (!filters[filterName]?.value) return [];

    return Array.isArray(filters[filterName].value)
      ? (filters[filterName].value as string[])
      : [filters[filterName].value as string];
  }, [filterName, filters]);

  if (!configFilter) return null;

  return (
    <TextFilterContainer>
      <AutoComplete
        value={value}
        placeholder={placeholder}
        options={variants || options}
        zIndex={100}
        width={width ? `${width}px` : "17.25rem"}
        menuHeight={height ? `${height}px` : "10rem"}
        withInfiniteScroll={!variants}
        infiniteScrollProps={variants ? undefined : infiniteScrollProps}
        multiSelect={multiSelect}
        selected={selected}
        setOptions={variants ? undefined : setOptions}
        onChange={setValue}
        onSelect={onSelect}
        onGetInnerRef={ref => (suggestRef.current = ref)}
        asyncCallback={variants ? undefined : search}
        onBlur={onBlur}
        onKeyUp={variants && !variants.length ? onKeyUp : undefined}
      />
    </TextFilterContainer>
  );
};
